/**
 * ContactAdminModal — floating "Contact admin" experience for every page.
 *
 * Flow:
 *   1) Fetch /admins/directory (public list of admins + editors)
 *   2) User multi-selects recipients, writes subject + message
 *   3) On submit → POST /admins/contact
 *      - server emails all recipients via Resend
 *      - server returns wa.me click-to-chat URLs for the ones with a
 *        WhatsApp number saved on their user document
 *   4) On success we render a small results screen with per-admin
 *      "Send WhatsApp to <name>" buttons so the user can (optionally)
 *      also nudge them on WhatsApp from their own device — zero cost,
 *      no API keys needed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { X, Loader2, MessageCircle, Mail, Check, ChevronDown, ChevronUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function ContactAdminModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // null | { email_sent_to, whatsapp_targets, ... }

  const isLoggedIn = !!user;

  // Load directory on open
  useEffect(() => {
    if (!open) return;
    setLoadingAdmins(true);
    http.get("/admins/directory")
      .then((r) => setAdmins(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Couldn't load admin directory"))
      .finally(() => setLoadingAdmins(false));
  }, [open]);

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSelectAll(false);
    setShowRoster(false);
    setSubject("");
    setMessage("");
    setFromName(user?.name || user?.full_name || "");
    setFromEmail(user?.email || "");
    setResult(null);
  }, [open, user]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === admins.length) return new Set();
      return new Set(admins.map((a) => a.id));
    });
    setSelectAll((s) => !s);
  }, [admins]);

  const canSubmit =
    !sending &&
    subject.trim().length >= 3 &&
    message.trim().length >= 5 &&
    (isLoggedIn || (fromName.trim() && fromEmail.trim())) &&
    selected.size > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const { data } = await http.post("/admins/contact", {
        admin_ids: Array.from(selected),
        subject:   subject.trim(),
        message:   message.trim(),
        from_name:  fromName.trim() || undefined,
        from_email: fromEmail.trim() || undefined,
        page_url:  typeof window !== "undefined" ? window.location.href : undefined,
      });
      setResult(data);
      if ((data.email_sent_to || []).length > 0) {
        toast.success(`Emailed ${data.email_sent_to.length} admin${data.email_sent_to.length === 1 ? "" : "s"}`);
      } else if ((data.email_failed_for || []).length > 0) {
        toast.error("Email delivery failed for all recipients — you can still WhatsApp them below.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const openAllWhatsApp = () => {
    for (const target of result?.whatsapp_targets || []) {
      window.open(target.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-admin-modal"
        className="max-w-lg max-h-[92dvh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#3D2A17]">
            <MessageCircle className="w-5 h-5" style={{ color: "#B25D38" }} />
            Contact admin
          </DialogTitle>
          <p className="text-sm text-neutral-600">
            We'll email the admins you pick right away. If they have a
            WhatsApp number on file, you can also tap them a note from
            your phone.
          </p>
        </DialogHeader>

        {result ? (
          <ResultScreen
            result={result}
            onCloseAll={() => onOpenChange?.(false)}
            onOpenAllWhatsApp={openAllWhatsApp}
          />
        ) : (
          <div className="space-y-4 mt-2">
            {/* Recipient picker */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[#3D2A17]">Recipients</label>
                <button
                  type="button"
                  onClick={toggleAll}
                  data-testid="contact-admin-select-all"
                  className="text-xs text-[#B25D38] hover:underline"
                >
                  {selected.size === admins.length && admins.length > 0 ? "Clear all" : "Select all"}
                </button>
              </div>
              {loadingAdmins ? (
                <div className="p-4 flex items-center justify-center text-sm text-neutral-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading admins…
                </div>
              ) : admins.length === 0 ? (
                <div className="p-4 text-sm text-neutral-500 bg-neutral-50 rounded-lg">
                  No admins are listed yet.
                </div>
              ) : (
                <ul className="divide-y border rounded-lg bg-white" data-testid="contact-admin-roster">
                  {admins.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#FFF8EC]">
                      <input
                        type="checkbox"
                        id={`admin-${a.id}`}
                        data-testid={`contact-admin-pick-${a.id}`}
                        checked={selected.has(a.id)}
                        onChange={() => toggle(a.id)}
                        className="w-4 h-4 accent-[#B25D38]"
                      />
                      <label htmlFor={`admin-${a.id}`} className="flex-1 cursor-pointer">
                        <div className="text-sm font-medium text-[#3D2A17]">{a.name}</div>
                        <div className="text-xs text-neutral-500 flex items-center gap-2">
                          <span className="uppercase tracking-wide">{a.role.replace("_", " ")}</span>
                          {a.has_email && <span className="inline-flex items-center gap-0.5"><Mail className="w-3 h-3" /> Email</span>}
                          {a.has_whatsapp && <span className="inline-flex items-center gap-0.5 text-emerald-700"><MessageCircle className="w-3 h-3" /> WhatsApp</span>}
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Subject + message */}
            <div>
              <label className="block text-sm font-medium text-[#3D2A17] mb-1">Subject</label>
              <input
                data-testid="contact-admin-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                placeholder="What is this about?"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-[#B25D38] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#3D2A17] mb-1">Message</label>
              <textarea
                data-testid="contact-admin-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                maxLength={5000}
                placeholder="Share what's on your mind — feedback, a bug, a request…"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-[#B25D38] focus:outline-none"
              />
            </div>

            {/* Anonymous sender info */}
            {!isLoggedIn && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#3D2A17] mb-1">Your name</label>
                  <input
                    data-testid="contact-admin-from-name"
                    type="text"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-[#B25D38] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3D2A17] mb-1">Reply-to email</label>
                  <input
                    data-testid="contact-admin-from-email"
                    type="email"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-[#B25D38] focus:outline-none"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={sending}>Cancel</Button>
              <Button
                data-testid="contact-admin-send"
                onClick={submit}
                disabled={!canSubmit}
                style={{ background: canSubmit ? "#B25D38" : "#D6C7A4", color: "#FFF" }}
              >
                {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…</> : <>Send to {selected.size || "…"} admin{selected.size === 1 ? "" : "s"}</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function ResultScreen({ result, onCloseAll, onOpenAllWhatsApp }) {
  const emailed = result.email_sent_to || [];
  const failed  = result.email_failed_for || [];
  const wa      = result.whatsapp_targets || [];
  return (
    <div className="space-y-4 mt-2" data-testid="contact-admin-result">
      {emailed.length > 0 && (
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <div className="text-sm font-medium text-emerald-800 flex items-center gap-2">
            <Check className="w-4 h-4" /> Email sent to {emailed.length} admin{emailed.length === 1 ? "" : "s"}
          </div>
          <ul className="text-xs text-emerald-700 mt-1 list-disc list-inside">
            {emailed.map((e) => <li key={e.id}>{e.name}</li>)}
          </ul>
        </div>
      )}
      {failed.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="text-sm font-medium text-amber-900">
            {failed.length} email{failed.length === 1 ? "" : "s"} couldn't be delivered. Try WhatsApp instead.
          </div>
        </div>
      )}
      {wa.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-[#3D2A17]">Also nudge on WhatsApp</div>
            {wa.length > 1 && (
              <button
                type="button"
                onClick={onOpenAllWhatsApp}
                data-testid="contact-admin-wa-open-all"
                className="text-xs text-emerald-700 hover:underline"
              >
                Open all
              </button>
            )}
          </div>
          <div className="grid gap-2">
            {wa.map((t) => (
              <a
                key={t.id}
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`contact-admin-wa-${t.id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-white hover:bg-emerald-50 text-sm text-emerald-800"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="flex-1">Send WhatsApp to <strong>{t.name}</strong></span>
                <span className="text-xs">Open →</span>
              </a>
            ))}
          </div>
        </div>
      )}
      {wa.length === 0 && emailed.length > 0 && (
        <div className="p-3 rounded-lg bg-neutral-50 border text-xs text-neutral-600">
          None of the selected admins have a WhatsApp number on file yet. They'll reply by email.
        </div>
      )}
      <div className="flex justify-end pt-2 border-t">
        <Button onClick={onCloseAll} style={{ background: "#B25D38", color: "#FFF" }} data-testid="contact-admin-close">
          Done
        </Button>
      </div>
    </div>
  );
}
